import PropTypes from 'prop-types';

import { getQuestionTypeName } from '@helpers';
import { Badge, ButtonText, Checkbox, Pill, PlusIcon, Select } from '@utilities';

import {
  NET_ELIGIBLE_QUESTION_TYPES,
  QUESTION_TYPE_BADGES,
  QUESTION_TYPE_METRIC_LABELS,
} from '../../utilities/constants';
import {
  getDefaultRankTopN,
  getQuestionLabel,
  getQuestionOptionValues,
  isRankQuestion,
} from '../../utilities/helpers';

import styles from './_index.module.scss';

const RankMetricSelect = ({ isDisabled, onChange, question, rankTopN }) => {
  const options = getQuestionOptionValues(question).map((_, index) => ({
    label: String(index + 1),
    value: index + 1,
  }));
  const value = rankTopN ?? getDefaultRankTopN(question);

  return (
    <span className={styles['tables-list-metric']}>
      Show % ranked in top
      <Select
        aria-label="Show % ranked in top"
        className={styles['tables-list-metric-select']}
        isDisabled={isDisabled}
        onChange={(option) => onChange(option.value)}
        options={options}
        value={options.find((option) => option.value === value) ?? null}
      />
    </span>
  );
};

RankMetricSelect.propTypes = {
  isDisabled: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  question: PropTypes.object.isRequired,
  rankTopN: PropTypes.number,
};

const TablesList = ({
  ineligibleQuestions,
  onEditNet,
  onRankTopNChange,
  onRemoveNet,
  onToggleQuestion,
  questionConfigs,
  questions,
}) => {
  const rows = [
    ...questions.map((question) => ({ isEligible: true, question })),
    ...ineligibleQuestions.map((question) => ({ isEligible: false, question })),
  ].sort((a, b) => a.question.index - b.question.index);

  return (
    <div className={styles['tables-list']} data-testid="tables-list">
      <ul>
        {rows.map(({ isEligible, question }) => {
          const label = getQuestionLabel(question);

          if (!isEligible) {
            return (
              <li
                className={`${styles['tables-list-row']} ${styles['tables-list-row-disabled']}`}
                key={question.id}
              >
                <div className={styles['tables-list-row-main']}>
                  <span className={styles['tables-list-label']} title={question.title}>
                    {label}
                  </span>
                  <Badge label={getQuestionTypeName(question.type) || question.type} />
                </div>
              </li>
            );
          }

          const config = questionConfigs.find(
            ({ questionId }) => questionId === String(question.id)
          );
          const metricLabel = QUESTION_TYPE_METRIC_LABELS[question.type];
          const isNetEligible = NET_ELIGIBLE_QUESTION_TYPES.includes(question.type);
          const hasOptions = getQuestionOptionValues(question).length >= 2;

          return (
            <li className={styles['tables-list-row']} key={question.id}>
              <div className={styles['tables-list-row-main']}>
                <div className={styles['tables-list-question']}>
                  <Checkbox
                    id={`crosstab-question-${question.id}`}
                    isChecked={Boolean(config?.included)}
                    label={<div className={styles['tables-list-label']}>{label}</div>}
                    onChange={() => onToggleQuestion(String(question.id))}
                    title={label}
                    value={String(question.id)}
                  />
                  {isRankQuestion(question) && (
                    <RankMetricSelect
                      isDisabled={!config?.included}
                      onChange={(rankTopN) => onRankTopNChange(String(question.id), rankTopN)}
                      question={question}
                      rankTopN={config?.rankTopN}
                    />
                  )}
                </div>
                <div className={styles['tables-list-row-meta']}>
                  {metricLabel && (
                    <span className={styles['tables-list-metric']}>{metricLabel}</span>
                  )}
                  <Badge label={QUESTION_TYPE_BADGES[question.type] ?? question.type} />
                </div>
              </div>
              {config?.included && isNetEligible && (
                <div className={styles['tables-list-nets']}>
                  {config.nets.map((net) => (
                    <Pill
                      data-testid={`row-net-${net.id}`}
                      isModified
                      key={net.id}
                      onClick={() => onEditNet(String(question.id), net.id)}
                      onClose={() => onRemoveNet(String(question.id), net.id)}
                      title={`Edit row net "${net.name}"`}
                      value={net.name}
                    />
                  ))}
                  <ButtonText
                    aria-disabled={!hasOptions}
                    className={
                      hasOptions
                        ? styles['tables-list-add-net']
                        : `${styles['tables-list-add-net']} ${styles['tables-list-add-net-disabled']}`
                    }
                    onClick={() => hasOptions && onEditNet(String(question.id), null)}
                    title={
                      hasOptions
                        ? 'Combine answer options into a named summary row'
                        : 'This question needs at least two answer options for a row net'
                    }
                  >
                    <PlusIcon />
                    <span>Add row net</span>
                  </ButtonText>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

TablesList.propTypes = {
  ineligibleQuestions: PropTypes.arrayOf(PropTypes.object).isRequired,
  onEditNet: PropTypes.func.isRequired,
  onRankTopNChange: PropTypes.func.isRequired,
  onRemoveNet: PropTypes.func.isRequired,
  onToggleQuestion: PropTypes.func.isRequired,
  questionConfigs: PropTypes.arrayOf(
    PropTypes.shape({
      included: PropTypes.bool.isRequired,
      nets: PropTypes.array.isRequired,
      questionId: PropTypes.string.isRequired,
      rankTopN: PropTypes.number,
    })
  ).isRequired,
  questions: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default TablesList;
